import { max } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import type { AggregateResponse, Modality, ModalityAggregate } from '../types';
import { formatThreshold } from '../lib/color';

const MODALITY_LABELS: Record<Modality, string> = {
  color: 'Color Δ',
  contrast: 'Contrast',
  pitch: 'Pitch'
};

interface Props {
  aggregate: AggregateResponse | null;
  selected: Modality;
  personalThreshold?: number;
}

export function CommonsMap({ aggregate, selected, personalThreshold }: Props) {
  const summary = aggregate?.modalities[selected];
  return (
    <section className="lab-panel commons-panel">
      <div className="panel-header">
        <div>
          <p className="eyebrow">anonymous commons</p>
          <h2>Population threshold map</h2>
        </div>
        <span className="pill">{aggregate?.total ?? 0} human runs</span>
      </div>
      {!summary || summary.count === 0 ? (
        <div className="empty-map">
          <p>No uploaded thresholds yet for {MODALITY_LABELS[selected]}. The first real signal is deliciously lonely.</p>
        </div>
      ) : (
        <>
          <Histogram summary={summary} modality={selected} personalThreshold={personalThreshold} />
          <div className="percentile-grid">
            <Metric label="p10" value={summary.p10} modality={selected} />
            <Metric label="median" value={summary.p50} modality={selected} />
            <Metric label="p90" value={summary.p90} modality={selected} />
          </div>
          <p className="commons-note">
            Aggregates use the most recent {aggregate.windowDays} days and expose bins/percentiles only. Individual trial summaries stay anonymous and device-keyed.
          </p>
        </>
      )}
    </section>
  );
}

function Histogram({ summary, modality, personalThreshold }: { summary: ModalityAggregate; modality: Modality; personalThreshold?: number }) {
  const width = 440;
  const height = 180;
  const pad = 24;
  const top = 16;
  const bins = summary.bins;
  const maxCount = max(bins, (b) => b.count) ?? 1;
  const x = scaleLinear()
    .domain([bins[0]?.lo ?? 0, bins.at(-1)?.hi ?? 1])
    .range([pad, width - pad]);
  const y = scaleLinear().domain([0, maxCount]).range([height - pad, top]);
  const personalX = personalThreshold === undefined ? null : x(personalThreshold);

  return (
    <svg className="histogram" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Histogram for ${modality} thresholds`}>
      <defs>
        <linearGradient id={`bar-gradient-${modality}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.92" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.18" />
        </linearGradient>
      </defs>
      <line x1={pad} x2={width - pad} y1={height - pad} y2={height - pad} className="axis" />
      {bins.map((bin, i) => {
        const x0 = x(bin.lo);
        const x1 = x(bin.hi);
        const barH = height - pad - y(bin.count);
        return <rect key={`${bin.lo}-${i}`} x={x0 + 2} y={y(bin.count)} width={Math.max(1, x1 - x0 - 4)} height={barH} rx="4" fill={`url(#bar-gradient-${modality})`} />;
      })}
      {summary.p50 !== null ? <line x1={x(summary.p50)} x2={x(summary.p50)} y1={top} y2={height - pad} className="median-line" /> : null}
      {personalX !== null && Number.isFinite(personalX) ? <line x1={personalX} x2={personalX} y1="8" y2={height - pad} className="you-line" /> : null}
      <text x={pad} y={height - 4} className="axis-label">{formatThreshold(modality, bins[0]?.lo ?? 0)}</text>
      <text x={width - pad} y={height - 4} className="axis-label end">{formatThreshold(modality, bins.at(-1)?.hi ?? 0)}</text>
    </svg>
  );
}

function Metric({ label, value, modality }: { label: string; value: number | null; modality: Modality }) {
  return (
    <div className="metric-card">
      <span>{label}</span>
      <strong>{value === null ? '—' : formatThreshold(modality, value)}</strong>
    </div>
  );
}
